import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from './index';
import { useEffect, useCallback } from 'react';
import { useState } from 'react';
import { PlaidData } from '@/api/weave-x';
import { getPlaidAggregatedData } from '@/api/weave-x';
import { getDocument, getProfile, ProfileData } from '@/api/custom';

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

const NO_PLAID_DATA_MESSAGE =
  'No Plaid data found for this user, please ask your investor to connect any Plaid accounts via Wavvest App';

export const usePlaidData = (uid: string | null | undefined) => {
  const [plaidData, setPlaidData] = useState<PlaidData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleError = (err: unknown): string => {
    if (err instanceof Error) {
      if (err.message.includes('404')) {
        return NO_PLAID_DATA_MESSAGE;
      }
      return err.message;
    }
    return 'An unknown error occurred';
  };

  const fetchPlaidData = useCallback(async () => {
    if (!uid) {
      setPlaidData(null);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await getPlaidAggregatedData(uid);
      if (!response) {
        setError(NO_PLAID_DATA_MESSAGE);
      } else {
        setPlaidData(response);
      }
    } catch (err) {
      const errorMessage = handleError(err);
      setError(errorMessage);
      setPlaidData(null);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!uid) {
        if (isMounted) {
          setPlaidData(null);
          setError(null);
        }
        return;
      }

      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }
        const data = await getPlaidAggregatedData(uid);
        if (isMounted) {
          if (!data) {
            setError(NO_PLAID_DATA_MESSAGE);
            setPlaidData(null);
            return;
          }
          setPlaidData(data);
        }
      } catch (err) {
        if (isMounted) {
          const errorMessage = handleError(err);
          setError(errorMessage);
          setPlaidData(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [uid]);

  return { plaidData, loading, error, refetch: fetchPlaidData };
};

export const useMeetingNotes = (fileKey: string | null | undefined) => {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleError = (err: unknown): string => {
    if (err instanceof Error) {
      if (err.message.includes('404')) {
        return 'No meeting notes found for this investor';
      }
      return err.message;
    }
    return 'An unknown error occurred';
  };

  const fetchMeetingNotes = useCallback(async () => {
    if (!fileKey) {
      setContent(null);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await getDocument(fileKey);
      setContent(response.document.content);
    } catch (err) {
      const errorMessage = handleError(err);
      setError(errorMessage);
      setContent(null);
    } finally {
      setLoading(false);
    }
  }, [fileKey]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!fileKey) {
        if (isMounted) {
          setContent(null);
          setError(null);
        }
        return;
      }

      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }
        const response = await getDocument(fileKey);
        if (isMounted) {
          setContent(response.document.content);
        }
      } catch (err) {
        if (isMounted) {
          const errorMessage = handleError(err);
          setError(errorMessage);
          setContent(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [fileKey]);

  return { content, loading, error, refetch: fetchMeetingNotes };
};

export const useRiaProfile = () => {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    const response = await getProfile();
    setProfile(response.profile);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (isMounted) {
        setLoading(true);
        setError(null);
      }
      const response = await getProfile();
      if (isMounted) {
        setProfile(response.profile);
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  return { profile, loading, error, refetch: fetchProfile };
};
